import './Grafo.scss'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Graph } from 'react-d3-graph'
import { IAccionistas, Personas } from '../../constants/personas'
import { Proyectos } from '../../constants/proyectos'
import { GrafoSearch } from './GrafoSearch/GrafoSearch'
import { Box, Grid, IconButton, Tooltip } from '@mui/material'
import PersonaSVG from '../../../assets/images/persona.svg'
import SocioSVG from '../../../assets/images/socio.svg'
import SocioSVGOn from '../../../assets/images/socios_on.svg'
import ProyectoSVG from '../../../assets/images/proyecto.svg'
import ProyectoSVGOn from '../../../assets/images/proyectos_on.svg'
import EmpresaSVG from '../../../assets/images/empresa.svg'
import EmpresaSVGOn from '../../../assets/images/empresas_on.svg'
import GrafoPlaceholder from '../../../assets/images/grafo_bg_placeholder.png'
import { Empresas, IEmpresas } from '../../constants/empresas'
import { GrafoInfoCard } from './GrafoInfoCard/GrafoInfoCard'
import { GrafoConfig, NodeData, SearchData } from './Grafo.constants'
// import UndoIcon from '@mui/icons-material/Undo'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
// import DeleteIcon from '@mui/icons-material/Delete'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { useTranslation } from 'react-i18next'
import { IProyectos } from '../../data/data.model'

export const Grafo = () => {
  const { t }: any = useTranslation()
  const [isMobile, setIsMobile] = useState(
    window.matchMedia('(max-width: 968px)').matches
  )
  const [focusNodeID, setFocusNodeId] = useState('')
  const [defaultSearchTerm, setDefaultSearchTerm] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchedNode, setSearchedNode] = useState<SearchData>()
  const [history, setHistory] = useState<SearchData[]>([])
  const [searchCurrentIndex, setSearchCurrentIndex] = useState<number | null>(
    null
  ) // Índice actual en el historial

  const [isFocusFromClick, setIsFocusFromClick] = useState(false)

  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [cardData, setCardData] = useState<
    IAccionistas | IEmpresas | IProyectos
  >()
  const [grafoData, setGrafoData] = useState<{
    nodes: NodeData[]
    links: any[]
  }>({
    nodes: [],
    links: [],
  })

  const getSVGTypeIcon = (nodeType: string, on = false): any => {
    switch (nodeType) {
      case 'persona':
        return PersonaSVG
      case 'empresa':
        return !on ? EmpresaSVG : EmpresaSVGOn
      case 'proyecto':
        return !on ? ProyectoSVG : ProyectoSVGOn
      case 'socio':
        return !on ? SocioSVG : SocioSVGOn
    }
  }

  const getPersonaSharePercentages = (
    percentages: string[],
    companyId?: string
  ) => {
    const finalPercentages: string[] = []

    if (percentages.length) {
      if (companyId) {
        finalPercentages.push(
          ...percentages.filter((percentage: string) => {
            const [pCompanyId, pValue] = percentage.split(':')

            return (
              pCompanyId === companyId &&
              Number(pValue.trim().replaceAll(',', '.')) > 1
            )
          })
        )
      } else {
        finalPercentages.push(
          // ...percentages.filter((percentage: string) => {
          //   const [pCompanyId, pValue] = percentage.split(':')

          //   return Number(pValue.trim().replaceAll(',', '.')) > 1
          // })
          ...percentages
        )
      }

      return finalPercentages.map((element) => {
        const [compnyId, percentage] = element.split(':')
        // const percentage = element.split(':')[1]
        const companyName =
          Empresas.find((empresa) => empresa.intern_id === compnyId)
            ?.company_name || 'Unknown Company'
        return `${companyName}: ${Number(percentage.replaceAll(',', '.')) < 1 ? '< 1' : percentage}%`
      })
    }

    return finalPercentages
  }


  const searchData = useMemo(() => {
    const sociosNodos: SearchData[] = Personas.map((persona) => ({
      id: persona.shareholder_intern_id,
      nodeType: 'socio',
      label: persona.shareholder_name,
      info: {
        ...persona,
        companies_name_share_percentage: getPersonaSharePercentages(
          persona.companies_id_share_percentage
        ),
      },
    }))
    const proyectosNodos: SearchData[] = Proyectos.map((proyecto) => ({
      id: proyecto.project_intern_id,
      nodeType: 'proyecto',
      label: proyecto.property_name_or_project_name,
      info: proyecto,
    }))
    const empresaNodos: SearchData[] = Empresas.map((empresa) => ({
      id: empresa.intern_id,
      nodeType: 'empresa',
      label: empresa.company_name,
      info: empresa,
    }))

    return [...sociosNodos, ...proyectosNodos, ...empresaNodos]
  }, [])

  useEffect(() => {
    setGrafoData({ nodes: [], links: [] })
    if (searchedNode) {
      const nodes = []
      const sociosNodos: NodeData[] = []
      const empresasNodos: NodeData[] = []
      const proyectosNodos: NodeData[] = []

      const nodeType = searchedNode.nodeType

      switch (nodeType) {
        case 'socio':
          const empresasSocio = Empresas.filter((empresa: IEmpresas) =>
            searchedNode.info.companies_id.includes(empresa.intern_id)
          )

          const uniqueProyectosEmpresas = new Set()
          // // busca los proyectos de las empresas relacionadas al socio
          // // y los agrega a un set para evitar duplicados
          empresasSocio.forEach((empresa: IEmpresas) => {
            Proyectos.filter((proyecto: IProyectos) =>
              proyecto.company_intern_id.includes(empresa.intern_id)
            )
              .map((proyecto: IProyectos) => proyecto.project_intern_id)
              .forEach((proyectoId: string) =>
                uniqueProyectosEmpresas.add(proyectoId)
              )
          })

          const proyectosEmpresa = Proyectos.filter((proyecto: IProyectos) =>
            Array.from(uniqueProyectosEmpresas).includes(
              proyecto.project_intern_id
            )
          )

          sociosNodos.push({
            ...searchedNode,
            svg: getSVGTypeIcon(nodeType, true),
          })

          empresasNodos.push(
            ...empresasSocio.map((empresa: IEmpresas) => ({
              id: empresa.intern_id,
              svg: getSVGTypeIcon('empresa'),
              label: empresa.company_name,
              info: empresa,
              nodeType: 'empresa',
            }))
          )

          proyectosNodos.push(
            ...proyectosEmpresa.map((proyecto: IProyectos) => ({
              id: proyecto.project_intern_id,
              svg: getSVGTypeIcon('proyecto'),
              label: proyecto.property_name_or_project_name,
              info: proyecto,
              nodeType: 'proyecto',
            }))
          )

          break
        case 'empresa':
          const empresaSocios = Personas.filter(
            (persona: IAccionistas) =>
              persona.companies_id
                // .map((relacion: string) => relacion.split(':')[0])
                .includes(searchedNode.info.intern_id) &&
              persona.companies_id.includes(searchedNode.info.intern_id) &&
              persona.companies_id_share_percentage.length > 0 &&
              persona.companies_id_share_percentage.some(
                (relacion) =>
                  Number(relacion.split(':')[1].replaceAll(',', '.')) > 1
              )
          )

          const empresaProyectos = Proyectos.filter((proyecto: IProyectos) =>
            searchedNode.info.projects.includes(proyecto.project_intern_id)
          )

          sociosNodos.push(
            ...empresaSocios.map((persona: IAccionistas) => ({
              id: persona.shareholder_intern_id,
              svg: getSVGTypeIcon('socio'),
              label: persona.shareholder_name,
              info: {
                ...persona,
                companies_name_share_percentage: getPersonaSharePercentages(
                  persona.companies_id_share_percentage,
                  searchedNode.info.intern_id
                ),
              },
              nodeType: 'socio',
            }))
          )

          empresasNodos.push({
            ...searchedNode,
            svg: getSVGTypeIcon(nodeType, true),
          })

          proyectosNodos.push(
            ...empresaProyectos.map((proyecto: IProyectos) => ({
              id: proyecto.project_intern_id,
              svg: getSVGTypeIcon('proyecto'),
              label: proyecto.property_name_or_project_name,
              info: proyecto,
              nodeType: 'proyecto',
            }))
          )

          break
        case 'proyecto':
          const proyectoEmpresas = Empresas.filter((empresa: IEmpresas) =>
            empresa.projects.some((proyectoId: string) =>
              searchedNode.info.project_intern_id.includes(proyectoId)
            )
          )

          const uniqueSociosEmpresas = new Set()
          proyectoEmpresas.forEach((empresa: IEmpresas) => {
            Personas.filter(
              (persona: IAccionistas) =>
                persona.companies_id.includes(empresa.intern_id) &&
                persona.companies_id_share_percentage.length > 0 &&
                persona.companies_id_share_percentage.some(
                  (relacion) =>
                    Number(relacion.split(':')[1].replaceAll(',', '.')) > 1
                )
            )
              .map((persona: IAccionistas) => persona.shareholder_intern_id)
              .forEach((personaId: string) =>
                uniqueSociosEmpresas.add(personaId)
              )
          })

          const sociosEmpresas = Personas.filter((persona: IAccionistas) =>
            Array.from(uniqueSociosEmpresas).includes(
              persona.shareholder_intern_id
            )
          )

          sociosNodos.push(
            ...sociosEmpresas.map((persona: IAccionistas) => ({
              id: persona.shareholder_intern_id,
              svg: getSVGTypeIcon('socio'),
              label: persona.shareholder_name,
              info: {
                ...persona,
                companies_name_share_percentage: getPersonaSharePercentages(
                  persona.companies_id_share_percentage
                ),
              },
              nodeType: 'socio',
            }))
          )

          proyectosNodos.push({
            ...searchedNode,
            svg: getSVGTypeIcon(nodeType, true),
          })

          empresasNodos.push(
            ...proyectoEmpresas.map((empresa: IEmpresas) => ({
              id: empresa.intern_id,
              svg: getSVGTypeIcon('empresa'),
              label: empresa.company_name,
              info: empresa,
              nodeType: 'empresa',
            }))
          )

          break
      }

      const linksSet = new Set<string>()

      proyectosNodos.forEach((proyecto) => {
        empresasNodos
          .filter((empresa) =>
            proyecto.info.company_intern_id.includes(empresa.info.intern_id)
          )
          .forEach((empresa) => {
            const linkKey = `${empresa.info.intern_id}?${proyecto.info.project_intern_id}`
            linksSet.add(linkKey)
          })
      })

      sociosNodos.forEach((persona) => {
        // const role =
        //   persona.info.companies_id.length > 0
        //     ? t(`relacion.${persona.info.companies_id[0]}`)
        //     : ''
        empresasNodos
          .filter((empresa) => {
            return persona.info.companies_id.includes(empresa.info.intern_id)
          })
          .forEach((empresa) => {
            const linkKey = `${persona.info.shareholder_intern_id}?${empresa.info.intern_id}`
            linksSet.add(linkKey)
          })
      })

      const links = Array.from(linksSet).map((link) => {
        const [actualLink, label] = link.split(':')
        const [source, target] = actualLink.split('?')
        return { source, target, label }
      })

      nodes.push(...sociosNodos, ...empresasNodos, ...proyectosNodos)

      setGrafoData({ nodes, links })
    }
  }, [searchedNode])

  useEffect(() => {
    if (grafoData && searchedNode?.id && !isFocusFromClick) {
      const nodesNumber = grafoData.nodes.length
      const timing =
        grafoData.nodes.length < 10
          ? grafoData.nodes.length * 200
          : grafoData.nodes.length * 100

      if (nodesNumber === 1) {
        setFocusNodeId(searchedNode?.id || '')

        return
      }

      setTimeout(() => {
        setFocusNodeId(searchedNode?.id || '')
      }, timing)
    }
  }, [grafoData])

  const containerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { offsetWidth, offsetHeight } = containerRef.current
        setDimensions({ width: offsetWidth, height: offsetHeight })
      }
    }

    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  const config = {
    ...GrafoConfig,
    width: dimensions.width,
    height: dimensions.height,
  }

  const resetStates = (avoidHistory = false) => {
    setDefaultSearchTerm('')
    setFocusNodeId('')
    setCardData(undefined)
    setSearchedNode(undefined)
    setGrafoData({ nodes: [], links: [] })
    setIsModalOpen(false)
    if (avoidHistory) return
    setHistory([])
    setSearchCurrentIndex(null) // Reinicia el índice al restablecer el estado
  }

  const onClickNode = (nodeId: any) => {
    setFocusNodeId(nodeId)
    setIsFocusFromClick(true)

    setGrafoData((prev) => {
      const newNodes = prev.nodes.map((node) => {
        if (node.id === nodeId) {
          // cambia el icono al nodo seleccionado
          return {
            ...node,
            svg: getSVGTypeIcon(node.nodeType, true),
          }
        } else {
          return {
            ...node,
            svg: getSVGTypeIcon(node.nodeType),
          }
        }
      })

      return { ...prev, nodes: newNodes }
    })

    const data = grafoData.nodes.find((node: NodeData) => node.id === nodeId)

    setCardData(data?.info)
    // setSearchedNode(nodeId)
    setIsModalOpen(true)
  }

  const handleDoubleClickNode = (nodeId: any) => {
    if (searchedNode?.id === nodeId) {
      return
    }

    resetStates(true)

    setTimeout(() => {
      const data: SearchData = searchData.find(
        (node: SearchData) => node.id === nodeId
      ) || { id: '', nodeType: '', label: '', info: {} }

      setHistory((prevHistory) => {
        const updatedHistory = [...prevHistory, data]

        setSearchCurrentIndex(updatedHistory.length - 1) // Establece el índice al nuevo último elemento
        return updatedHistory
      })

      setCardData(data?.info)
      setDefaultSearchTerm(data?.label || '')
      setSearchedNode(data)
    }, 500)
  }

  const handleSearchClick = (target: any) => {
    setHistory((prevHistory) => {
      const updatedHistory = [...prevHistory, target]

      setSearchCurrentIndex(updatedHistory.length - 1) // Establece el índice al nuevo último elemento
      return updatedHistory
    })
    setIsSearching(true)

    setSearchedNode(target)
  }

  const handleSearchBack = () => {
    if (searchCurrentIndex !== null && searchCurrentIndex > 0) {
      const previousIndex = searchCurrentIndex - 1
      setSearchedNode(history[previousIndex])
      setSearchCurrentIndex(previousIndex) // Actualiza al índice anterior
      setDefaultSearchTerm(history[previousIndex].label)
    }
  }

  return (
    <Grid
      container
      sx={{
        marginBottom: '20px',
        border: '1px solid black',
        position: 'relative',
      }}
    >
      <Grid item xs={12} md={3} className="searchLegendContainer">
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px',
            }}
          >
            <Box className="grafo-search-icons">
              <Tooltip title={t('graph.previous_search')}>
                <IconButton
                  aria-label="previous_search"
                  onClick={handleSearchBack}
                  disabled={!searchedNode || searchCurrentIndex === 0}
                >
                  <KeyboardBackspaceIcon sx={{ fontSize: 28, color: '#fff' }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box className="grafo-search-icons">
              <Tooltip title={t('graph.clear_search')}>
                <IconButton
                  aria-label="restore"
                  onClick={() => {
                    setDefaultSearchTerm('')
                    setIsSearching(false)
                    resetStates()
                  }}
                  disabled={!searchedNode}
                >
                  <DeleteForeverIcon sx={{ fontSize: 28, color: '#fff' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box>
            {/* <Box sx={{ position: 'absolute', top: 65, left: 23 }}> */}
            <GrafoSearch
              nodes={searchData}
              handleSearchClick={handleSearchClick}
              defaultSearchTerm={defaultSearchTerm}
              setDefaultSearchTerm={setDefaultSearchTerm}
              setIsFocusFromClick={setIsFocusFromClick}
            ></GrafoSearch>
          </Box>
        </Box>
        <Box className="grafo-leyenda-container" display="flex" justifyContent="space-between" alignItems="center">
          <ul className="grafo-leyenda">
            {/* <li>
            <img src={PersonaSVG} alt="Persona" />
            Persona
          </li> */}
            <li>
              <img src={ProyectoSVG} alt="Proyecto" />
              {t('graph.project')}
            </li>
            <li>
              <img src={EmpresaSVG} alt="Empresa" />
              {t('graph.company')}
            </li>
            <li>
              <img src={SocioSVG} alt="Socio" />
              {t('graph.partner')}
            </li>
          </ul>
          <p className="graph-note ">
            {t('graph.note')}
          </p>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        md={9}
        style={{ padding: '0px', backgroundColor: 'rgba(216, 227, 227, 0.4)' }}
      >
        <Box
          ref={containerRef}
          sx={{
            // border: '1px solid black',
            height: !isMobile ? '800px' : '500px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            position: 'relative',
          }}
        >
          {searchedNode || isSearching ? (
            <Graph
              id="graph-id"
              data={{ ...grafoData, focusedNodeId: focusNodeID }}
              config={config}
              onClickNode={onClickNode}
              onDoubleClickNode={handleDoubleClickNode}
            />
          ) : (
            <>
              <span
                style={{
                  position: 'absolute',
                  top: '52.5%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: '#000',
                  fontSize: '16px',
                  textAlign: 'center',
                  padding: '5px 10px',
                  // backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  maxWidth: 180,
                }}
              >
                {t('graph.searchText')}
              </span>
              <img
                style={{ width: '80%', maxWidth: 445 }}
                src={GrafoPlaceholder}
                alt=""
              />
            </>
          )}
        </Box>
      </Grid>
      {isModalOpen ? (
        <GrafoInfoCard data={cardData} setIsModalOpen={setIsModalOpen} />
      ) : null}
    </Grid>
  )
}
